import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogBody,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import Input from "../ui/input";
import DatePicker from "../shared-modules/DatePicker/DatePicker";
import Button from "../button-v1";
import useCurrentTime from "../shared-modules/DatePicker/useCurrentTime";
import styles from "./AddEventModal.module.css";

import { createPatientReadingEvent } from '../../services/patient/details'


const eventSchema = z.object({
  description: z
    .string()
    .min(1, "Description is required")
    .min(3, "Description must be at least 3 characters"),
});


const AddEventModal = ({ isOpen, onClose, tenant, onSubmitSuccess, readingType }) => {
  const curr = useCurrentTime();
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateError, setDateError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(eventSchema),
    defaultValues: {
      description: "",
    },
  });



useEffect(() => {
  if (isOpen && selectedDate === null) {
    setSelectedDate(curr); 
  } else if (!isOpen) {
    reset();
    setSelectedDate(null);
    setDateError(false);
    setApiError("");
  }
}, [isOpen, reset, curr, selectedDate]);



  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (date) setDateError(false);
  };

  const getCurrentUserName = () => {
    try {
      const userString = localStorage.getItem("user");
      if (!userString) return "Unknown User";
      const user = JSON.parse(userString);
      if (user.first_name || user.last_name) {
        return `${user.first_name || ""} ${user.last_name || ""}`.trim();
      }
      return user.username || user.email || "Unknown User";
    } catch (error) {
      console.error("Error getting user name:", error);
      return "Unknown User";
    }
  };
  
  const handleFormSubmit = async (data) => {
    if (!selectedDate) {
      setDateError(true);
      return;
    }

    const patientId = sessionStorage.getItem("_pt_id");
    if (!patientId) {
      setApiError("Patient ID not found.");
      return;
    }

    const userName = getCurrentUserName(); 
    const readingTypeWithUser = `${readingType}||${userName}`;

    const payload = {
      patient: patientId,
      reading_type: readingTypeWithUser,
      description: data.description,
      event_date: new Date(selectedDate).toISOString(),
    };

    try {
      setLoading(true);
      setApiError("");
      const response = await createPatientReadingEvent(payload);
      if (onSubmitSuccess) onSubmitSuccess(response);
      reset();
      setSelectedDate(null);
      onClose();
    } catch (error) {
      setApiError(error.message);
    } finally {
      setLoading(false);
    }
  };



  return (
    <Dialog open={isOpen} setOpen={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Event</DialogTitle>
        </DialogHeader>

        <DialogBody>
          {apiError && <p className={styles.errorMessage}>{apiError}</p>}

          <form onSubmit={handleSubmit(handleFormSubmit)} className={styles.form}>
            <div className={styles.input_group}>
              <div className={styles.input_wrapper}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Input
                        {...field}
                        label="Description"
                        placeholder="Enter event description"
                        required
                        error={errors.description?.message}
                      />
                      {errors.description && (
                        <p className={styles.errorMessage}>
                          {errors.description.message}
                        </p>
                      )}
                    </>
                  )}
                />
              </div>

              <div className={styles.input_wrapper}>
                <label>Date</label>
                <DatePicker
                  selectedDate={selectedDate || curr}
                  onDateSelect={handleDateChange}
                  showTime={true}
                  disableFutureDates={false}
                  onCancelSelect={() => setSelectedDate(null)}
                />
                {dateError && <p className={styles.errorMessage}>Date is required</p>}
              </div>
            </div>
          </form>
        </DialogBody>

        <DialogFooter>
          <Button variant="normal" size="default" onClick={onClose}>
            Close
          </Button>

          <Button
            variant="primary"
            size="default"
            disabled={loading}
            onClick={handleSubmit(handleFormSubmit)}
          >
            {loading ? "Saving..." : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddEventModal;
